import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MessageSquarePlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { createUpdateRequest } from '../../../api/eqmsApi';
import { EQMS_QUERY_KEYS } from '../../../hooks/useEqmsDocuments';

interface RequestUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  documentLabel: string;
  /** Unknown to training readers; the copy then names "the document owner". */
  ownerName?: string | null;
}

export function RequestUpdateDialog({ open, onOpenChange, documentId, documentLabel, ownerName }: RequestUpdateDialogProps) {
  const { t } = useTranslation('qms');
  const queryClient = useQueryClient();
  const owner = ownerName || t('updateRequests.theOwner');
  const [reason, setReason] = useState('');
  // One key per opened dialog: a retried or double-clicked submit replays the
  // same request instead of filing a second one.
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  useEffect(() => {
    if (!open) return;
    setReason('');
    setRequestKey(crypto.randomUUID());
  }, [open]);

  const submit = useMutation({
    retry: false,
    mutationFn: () => createUpdateRequest({ documentId, reason: reason.trim(), requestKey }),
    onSuccess: () => {
      toast.success(t('updateRequests.sent', { owner }));
      void queryClient.invalidateQueries({ queryKey: EQMS_QUERY_KEYS.updateRequests });
      onOpenChange(false);
    },
  });
  const trimmed = reason.trim();
  const valid = trimmed.length >= 3 && trimmed.length <= 2000;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submit.isPending) onOpenChange(next); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
            <MessageSquarePlus className="h-5 w-5" />
          </div>
          <DialogTitle>{t('updateRequests.dialogTitle')}</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-slate-800">{documentLabel}</span>
            {' — '}
            {ownerName ? t('updateRequests.dialogDescription', { owner: ownerName }) : t('updateRequests.dialogDescriptionGeneric')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="eqms-update-request-reason">{t('updateRequests.reason')}</Label>
          <Textarea
            id="eqms-update-request-reason"
            value={reason}
            rows={5}
            maxLength={2000}
            disabled={submit.isPending}
            placeholder={t('updateRequests.reasonHint')}
            onChange={(event) => setReason(event.target.value)}
            aria-describedby="eqms-update-request-help"
          />
          <p id="eqms-update-request-help" className="text-xs text-muted-foreground">{t('updateRequests.reasonHelp')}</p>
          {submit.isError && <p role="alert" className="text-sm text-red-700">{submit.error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={submit.isPending} onClick={() => onOpenChange(false)}>{t('updateRequests.cancel')}</Button>
          <Button className="bg-teal-700 hover:bg-teal-800" disabled={!valid || submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? t('updateRequests.sending') : t('updateRequests.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
